import { MultiSelectDropdown } from '@cc-shopping/ui/composites/MultiSelectDropdown';
import { memo, useCallback } from 'react';
import { useFiltersAnalytics } from 'src/features/roundup-filters/store/hooks/useFiltersAnalytics';
import { useShallow } from 'zustand/shallow';
import {
  CREDIT_RANGE_LABEL_TO_VALUE,
  CREDIT_RANGE_OPTIONS_LABEL_MAP,
  type CreditRangeFilterLabel,
} from '../../../config';
import { useOffersFilterStore } from '../../../store/Context';
import { FilterType } from '../../../store/enums';
import type { OffersFilterStoreState } from '../../../store/types';
import type { FilterComponentProps } from './types';
import { createApplyAndNotify } from './utils';

export const CreditRangeFilter = memo(
  ({ options, forceClose, onAfterChange, labelledById }: FilterComponentProps) => {
    const { creditRangeFilter, handleSingleFormUpdate, handleApplyFilters } = useOffersFilterStore(
      useShallow((state: OffersFilterStoreState) => ({
        creditRangeFilter: state.filterFormState.creditRangeFilter,
        handleSingleFormUpdate: state.handleSingleFormUpdate,
        handleApplyFilters: state.handleApplyFilters,
      })),
    );

    const { fireCreditRangeUpdateEvents } = useFiltersAnalytics();

    const selectedLabels = creditRangeFilter.map(range => CREDIT_RANGE_OPTIONS_LABEL_MAP[range]);

    const handleChange = useCallback(
      (newSelected: CreditRangeFilterLabel[]) => {
        const values = newSelected.map(sel => CREDIT_RANGE_LABEL_TO_VALUE[sel]).filter(Boolean);
        createApplyAndNotify(
          handleSingleFormUpdate,
          handleApplyFilters,
          onAfterChange,
        )({ type: FilterType.CREDIT_RANGE, value: values });
        fireCreditRangeUpdateEvents({
          prevSelectedValues: creditRangeFilter,
          newSelectedValues: values,
        });
      },
      [handleSingleFormUpdate, handleApplyFilters, onAfterChange, fireCreditRangeUpdateEvents],
    );

    return (
      <MultiSelectDropdown
        label="Credit range"
        labelledById={labelledById}
        placeholder="All credit ranges"
        options={options.creditRangeOptions as { label: CreditRangeFilterLabel; count: number }[]}
        selectedOptions={selectedLabels}
        onChange={handleChange}
        forceClose={forceClose}
      />
    );
  },
);
